import * as THREE from "three";
import { TempVec } from "../perf/TempVec";

export interface ParticleEmitOptions {
  origin: THREE.Vector3;
  count: number;
  color: THREE.Color | number;
  speed: { min: number; max: number };
  spread: number;
  direction?: THREE.Vector3;
  lifetime: { min: number; max: number };
  size: { min: number; max: number };
  gravity?: number;
  drag?: number;
}

export interface ParticleSystemOptions {
  capacity?: number;
}

const DEFAULT_CAPACITY = 256;

const VERT = `
attribute vec3 aColor;
attribute float aLife;
attribute float aSize;
varying vec3 vColor;
varying float vLife;
void main() {
  vColor = aColor;
  vLife = aLife;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  // Size attenuation: scale by inverse view distance.
  gl_PointSize = aSize * (300.0 / -mv.z);
}
`;

const FRAG = `
varying vec3 vColor;
varying float vLife;
void main() {
  if (vLife <= 0.0) discard;
  vec2 uv = gl_PointCoord - vec2(0.5);
  float r = length(uv);
  if (r > 0.5) discard;
  // Soft circle, brightest in the middle.
  float alpha = (1.0 - r * 2.0);
  alpha *= alpha;
  // Fade out across the particle's life (vLife runs 1 -> 0).
  alpha *= vLife;
  gl_FragColor = vec4(vColor, alpha);
}
`;

// GPU-pooled point emitter. Fixed capacity, zero per-frame allocations.
// Particles age via a velocity + gravity + drag integrator, fade via the
// fragment shader using a per-vertex `aLife` attribute.
//
// The emitter walks the pool to find dead slots; if all slots are alive
// the oldest slots are overwritten (acceptable for cosmetic VFX). Updated
// attribute ranges are flagged so we only re-upload the touched window.
export class ParticleSystem {
  readonly object: THREE.Points;
  private capacity: number;

  // Per-particle state stored in TypedArrays for cache locality.
  private position: Float32Array;
  private velocity: Float32Array;
  private color: Float32Array;
  private life: Float32Array;        // 0..1 normalized remaining life
  private maxLife: Float32Array;     // total lifetime in seconds
  private size: Float32Array;
  private gravity: Float32Array;
  private drag: Float32Array;
  private lifeAge: Float32Array;     // seconds elapsed since spawn

  private positionAttr: THREE.BufferAttribute;
  private colorAttr: THREE.BufferAttribute;
  private lifeAttr: THREE.BufferAttribute;
  private sizeAttr: THREE.BufferAttribute;

  private nextOverwriteIdx = 0;

  constructor(opts: ParticleSystemOptions = {}) {
    this.capacity = Math.max(8, Math.floor(opts.capacity ?? DEFAULT_CAPACITY));
    const cap = this.capacity;

    this.position = new Float32Array(cap * 3);
    this.velocity = new Float32Array(cap * 3);
    this.color = new Float32Array(cap * 3);
    this.life = new Float32Array(cap);
    this.maxLife = new Float32Array(cap);
    this.size = new Float32Array(cap);
    this.gravity = new Float32Array(cap);
    this.drag = new Float32Array(cap);
    this.lifeAge = new Float32Array(cap);

    const geo = new THREE.BufferGeometry();
    this.positionAttr = new THREE.BufferAttribute(this.position, 3);
    this.positionAttr.setUsage(THREE.DynamicDrawUsage);
    this.colorAttr = new THREE.BufferAttribute(this.color, 3);
    this.colorAttr.setUsage(THREE.DynamicDrawUsage);
    this.lifeAttr = new THREE.BufferAttribute(this.life, 1);
    this.lifeAttr.setUsage(THREE.DynamicDrawUsage);
    this.sizeAttr = new THREE.BufferAttribute(this.size, 1);
    this.sizeAttr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute("position", this.positionAttr);
    geo.setAttribute("aColor", this.colorAttr);
    geo.setAttribute("aLife", this.lifeAttr);
    geo.setAttribute("aSize", this.sizeAttr);
    geo.setDrawRange(0, cap);

    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
    });

    this.object = new THREE.Points(geo, mat);
    this.object.frustumCulled = false;
    this.object.renderOrder = 5;
  }

  emit(opts: ParticleEmitOptions): void {
    const cap = this.capacity;
    const tmpDir = TempVec.vec3();
    const tmpAxis = TempVec.vec3();
    const baseDir = opts.direction
      ? TempVec.vec3().copy(opts.direction).normalize()
      : null;
    const c = toColor(opts.color);
    const cr = c.r, cg = c.g, cb = c.b;
    let lifeMin = opts.lifetime.min;
    let lifeMax = opts.lifetime.max;
    if (lifeMax < lifeMin) lifeMax = lifeMin;
    let sizeMin = opts.size.min;
    let sizeMax = opts.size.max;
    if (sizeMax < sizeMin) sizeMax = sizeMin;
    const gravity = opts.gravity ?? 0;
    const drag = opts.drag ?? 0;

    let toEmit = Math.min(opts.count, cap);
    let emitted = 0;
    let i = 0;
    let touchedFirst = -1;
    let touchedLast = -1;

    while (toEmit > 0 && i < cap * 2) {
      const idx = i % cap;
      i++;
      const slotAlive = this.life[idx]! > 0;
      if (slotAlive && i <= cap) {
        // Skip alive slots on the first pass; if we wrap around, we'll
        // start overwriting (oldest first by walking nextOverwriteIdx).
        continue;
      }
      // Compute random direction within a cone of half-angle `spread`
      // around `baseDir` (or random sphere if baseDir is null).
      if (baseDir) {
        sampleConeDirection(baseDir, opts.spread, tmpDir, tmpAxis);
      } else {
        sampleSphereDirection(tmpDir);
      }
      const speed = lerp(opts.speed.min, opts.speed.max, Math.random());
      const px = opts.origin.x;
      const py = opts.origin.y;
      const pz = opts.origin.z;
      const i3 = idx * 3;
      this.position[i3] = px;
      this.position[i3 + 1] = py;
      this.position[i3 + 2] = pz;
      this.velocity[i3] = tmpDir.x * speed;
      this.velocity[i3 + 1] = tmpDir.y * speed;
      this.velocity[i3 + 2] = tmpDir.z * speed;
      this.color[i3] = cr;
      this.color[i3 + 1] = cg;
      this.color[i3 + 2] = cb;
      const lt = lerp(lifeMin, lifeMax, Math.random());
      this.maxLife[idx] = lt;
      this.lifeAge[idx] = 0;
      this.life[idx] = 1;
      this.size[idx] = lerp(sizeMin, sizeMax, Math.random());
      this.gravity[idx] = gravity;
      this.drag[idx] = drag;

      if (touchedFirst < 0 || idx < touchedFirst) touchedFirst = idx;
      if (idx > touchedLast) touchedLast = idx;
      emitted++;
      toEmit--;
    }

    if (emitted < opts.count) {
      // Pool exhausted; overwrite the oldest slots.
      for (let k = 0; k < opts.count - emitted; k++) {
        const idx = this.nextOverwriteIdx;
        this.nextOverwriteIdx = (this.nextOverwriteIdx + 1) % cap;
        if (baseDir) sampleConeDirection(baseDir, opts.spread, tmpDir, tmpAxis);
        else sampleSphereDirection(tmpDir);
        const speed = lerp(opts.speed.min, opts.speed.max, Math.random());
        const i3 = idx * 3;
        this.position[i3] = opts.origin.x;
        this.position[i3 + 1] = opts.origin.y;
        this.position[i3 + 2] = opts.origin.z;
        this.velocity[i3] = tmpDir.x * speed;
        this.velocity[i3 + 1] = tmpDir.y * speed;
        this.velocity[i3 + 2] = tmpDir.z * speed;
        this.color[i3] = cr;
        this.color[i3 + 1] = cg;
        this.color[i3 + 2] = cb;
        const lt = lerp(lifeMin, lifeMax, Math.random());
        this.maxLife[idx] = lt;
        this.lifeAge[idx] = 0;
        this.life[idx] = 1;
        this.size[idx] = lerp(sizeMin, sizeMax, Math.random());
        this.gravity[idx] = gravity;
        this.drag[idx] = drag;
        if (touchedFirst < 0 || idx < touchedFirst) touchedFirst = idx;
        if (idx > touchedLast) touchedLast = idx;
      }
    }

    if (touchedFirst >= 0) {
      this.markRange(touchedFirst, touchedLast - touchedFirst + 1);
    }
  }

  update(dt: number): void {
    if (dt <= 0) return;
    const cap = this.capacity;
    let firstAlive = -1;
    let lastAlive = -1;
    for (let idx = 0; idx < cap; idx++) {
      if (this.life[idx]! <= 0) continue;
      const i3 = idx * 3;
      let vx = this.velocity[i3]!;
      let vy = this.velocity[i3 + 1]!;
      let vz = this.velocity[i3 + 2]!;
      vy -= this.gravity[idx]! * dt;
      const dragK = Math.max(0, 1 - this.drag[idx]! * dt);
      vx *= dragK;
      vy *= dragK;
      vz *= dragK;
      this.velocity[i3] = vx;
      this.velocity[i3 + 1] = vy;
      this.velocity[i3 + 2] = vz;
      this.position[i3] += vx * dt;
      this.position[i3 + 1] += vy * dt;
      this.position[i3 + 2] += vz * dt;
      this.lifeAge[idx] += dt;
      const lifeRemaining = 1 - this.lifeAge[idx]! / this.maxLife[idx]!;
      this.life[idx] = lifeRemaining > 0 ? lifeRemaining : 0;
      if (firstAlive < 0) firstAlive = idx;
      lastAlive = idx;
    }
    // Push everything (positions + lives) for the touched range. Colors
    // and sizes don't change post-emit, so we only flag them in `emit()`.
    if (firstAlive >= 0) {
      const start = firstAlive;
      const count = lastAlive - firstAlive + 1;
      this.positionAttr.addUpdateRange(start * 3, count * 3);
      this.positionAttr.needsUpdate = true;
      this.lifeAttr.addUpdateRange(start, count);
      this.lifeAttr.needsUpdate = true;
    }
  }

  dispose(): void {
    this.object.geometry.dispose();
    (this.object.material as THREE.ShaderMaterial).dispose();
  }

  private markRange(start: number, count: number): void {
    this.positionAttr.addUpdateRange(start * 3, count * 3);
    this.positionAttr.needsUpdate = true;
    this.colorAttr.addUpdateRange(start * 3, count * 3);
    this.colorAttr.needsUpdate = true;
    this.lifeAttr.addUpdateRange(start, count);
    this.lifeAttr.needsUpdate = true;
    this.sizeAttr.addUpdateRange(start, count);
    this.sizeAttr.needsUpdate = true;
  }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function toColor(c: THREE.Color | number): THREE.Color {
  if (typeof c === "number") return new THREE.Color(c);
  return c;
}

function sampleSphereDirection(out: THREE.Vector3): void {
  // Uniform random direction on the unit sphere.
  const u = Math.random() * 2 - 1;
  const theta = Math.random() * Math.PI * 2;
  const r = Math.sqrt(1 - u * u);
  out.set(r * Math.cos(theta), r * Math.sin(theta), u);
}

function sampleConeDirection(
  axis: THREE.Vector3,
  halfAngle: number,
  out: THREE.Vector3,
  scratchPerp: THREE.Vector3
): void {
  // Sample a direction within `halfAngle` of `axis`. Uniform on the
  // spherical cap.
  const cosA = Math.cos(halfAngle);
  const z = lerp(cosA, 1, Math.random());
  const phi = Math.random() * Math.PI * 2;
  const r = Math.sqrt(Math.max(0, 1 - z * z));
  const localX = r * Math.cos(phi);
  const localY = r * Math.sin(phi);
  // Build an orthonormal basis with `axis` as the local +Z.
  const ax = Math.abs(axis.x);
  const ay = Math.abs(axis.y);
  const az = Math.abs(axis.z);
  if (ax <= ay && ax <= az) scratchPerp.set(0, -axis.z, axis.y);
  else if (ay <= ax && ay <= az) scratchPerp.set(-axis.z, 0, axis.x);
  else scratchPerp.set(-axis.y, axis.x, 0);
  scratchPerp.normalize();
  // Second perpendicular = axis x scratchPerp (cross product, inline to
  // avoid creating extra Vector3s).
  const bx = axis.y * scratchPerp.z - axis.z * scratchPerp.y;
  const by = axis.z * scratchPerp.x - axis.x * scratchPerp.z;
  const bz = axis.x * scratchPerp.y - axis.y * scratchPerp.x;
  out.set(
    scratchPerp.x * localX + bx * localY + axis.x * z,
    scratchPerp.y * localX + by * localY + axis.y * z,
    scratchPerp.z * localX + bz * localY + axis.z * z
  );
}
